import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { formatDate } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button, Checkbox, Icon, Loader, Table } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { type FollowingItem, parseFollowingItem } from "./types";

const FollowingContainer = styled.div`
  gap: 10px;
  padding-bottom: 10px;
  > h3 {
    margin: 0;
  }
  > span {
    display: block;
    margin: 10px 0;
  }
  .search-options {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
  }
  .pagination-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .following-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    > span,
    a {
      display: block;
    }
    a {
      color: black;
      &:not(.following-is-deleted) {
        text-decoration-line: underline;
      }
    }
    a.following-is-deleted {
      pointer-events: none;
    }
    .following-type {
      opacity: 0.65;
      font-size: 0.8em;
      height: 15px;
    }
    .following-name {
      font-weight: 500;
    }
  }
`;

const FollowingContext = styled.div`
  display: flex;
  flex-direction: row;
  .following-image {
    object-fit: cover;
    width: 40px;
    height: 40px;
    border-radius: 5px;
    margin-right: 10px;
  }
  i.icon {
    width: 40px;
    height: 40px;
    font-size: 2.75em;
    margin-right: 10px;
    color: gray;
  }
`;

type ProfileFollowingProps = {
  profileUser: OpenAPI.Schemas["User"];
  loading: boolean;
};

const ProfileFollowing = ({
  profileUser,
  loading: profileLoading,
}: ProfileFollowingProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState("");
  const [following, setFollowing] = useState<FollowingItem[]>([]);
  const [hasFollowing, setHasFollowing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showUnfollowed, setShowUnfollowed] = useState(false);
  const [paginationState, setPaginationState] = useState({
    prevPageAvailable: false,
    nextPageAvailable: false,
  });
  const profileUserId = profileUser._id;

  const getFollowing = useCallback(() => {
    if (profileUserId) {
      setLoading(true);
      api.users.getFollowing(
        profileUserId,
        { query: searchQuery, page: searchPage, showDeleted, showUnfollowed },
        ({ following: newFollowing, prevPageAvailable, nextPageAvailable }) => {
          setFollowing(newFollowing.map(parseFollowingItem));
          setHasFollowing((prev) => prev || newFollowing.length > 0);
          setPaginationState({ nextPageAvailable, prevPageAvailable });
          setLoading(false);
        },
        (err) => {
          toast.error(`Failed to get user following: ${err.message}`);
          setLoading(false);
        },
      );
    }
  }, [profileUserId, searchQuery, searchPage, showDeleted, showUnfollowed]);

  const updateFollowing = useCallback((followingItem: FollowingItem) => {
    setUpdating(followingItem._id);
    const followApi =
      followingItem.type === "challenge" ? api.challenges : api.ideas;
    const onSuccess = () => {
      setFollowing((prevFollowing) =>
        prevFollowing.map((f) => {
          if (f._id !== followingItem._id) return f;
          return { ...f, unfollowed: !followingItem.unfollowed };
        }),
      );
      setUpdating("");
    };
    const onError = () => {
      toast.error("Failed to update following status");
      setUpdating("");
    };

    if (followingItem.unfollowed) {
      followApi.follow(followingItem._id, onSuccess, onError);
    } else {
      followApi.unfollow(followingItem._id, onSuccess, onError);
    }
  }, []);

  const getFollowingItemImage = useCallback((followingItem: FollowingItem) => {
    if (followingItem.type === "challenge")
      return followingItem.ownerChallenge?.bannerImageUrl || "";
    if (followingItem.type === "idea")
      return followingItem.ownerIdea ?
          util.ideaCoverImage(followingItem.ownerIdea, "200x200")
        : "";
    return "";
  }, []);

  const getFollowingItemTitle = useCallback((followingItem: FollowingItem) => {
    if (followingItem.type === "challenge")
      return followingItem.ownerChallenge?.name || "[DELETED]";
    if (followingItem.type === "idea")
      return followingItem.ownerIdea?.name || "[DELETED]";
    return "";
  }, []);

  const getReasonText = (followingItem: FollowingItem) => {
    const subject =
      user?._id === profileUser._id ? "You" : profileUser.profile?.firstName;
    if (
      followingItem.type === "challenge" &&
      followingItem.reason === "ideaCreated"
    ) {
      return `${subject} created an idea in this ${t("generic.challenge")}`;
    }
    const translatedType = t(`generic.${followingItem.type}`);
    return `${subject} ${followingItem.reasonText} ${translatedType}`;
  };

  useEffect(() => getFollowing(), [getFollowing]);

  if (profileLoading) return <Loader active />;
  return (
    <FollowingContainer>
      <span>{t("users.following.info")}</span>
      {hasFollowing ?
        <div className="search-options">
          <Input
            icon={<Icon name="search" />}
            size="small"
            placeholder={t("users.following.search")}
            onChange={(_e, { value }) => setSearchQuery(value)}
            value={searchQuery || ""}
            loading={!!(loading && searchQuery)}
          />
          <Checkbox
            checked={showUnfollowed}
            onChange={() => setShowUnfollowed(!showUnfollowed)}
            label={`Show ${t("generic.ideas")}/challenges ${user?._id === profileUser._id ? "you have" : `${profileUser.profile?.firstName} has`} unfollowed`}
          />
          {(
            util.hasPermission(
              user,
              "org.viewDashboard",
              user?.ownerOrganisation?._id,
            )
          ) ?
            <Checkbox
              checked={showDeleted}
              onChange={() => setShowDeleted(!showDeleted)}
              label={t("users.profile.following.deleted")}
            />
          : null}
        </div>
      : null}
      {loading && !hasFollowing ?
        <Loader active />
      : <Table basic="very" padded style={{ width: "100%" }}>
          <Table.Body>
            {following.map((followingItem) => (
              <Table.Row key={followingItem._id}>
                <Table.Cell width={9}>
                  <FollowingContext>
                    {getFollowingItemImage(followingItem) ?
                      <img
                        className="following-image"
                        alt="Following item cover"
                        src={getFollowingItemImage(followingItem)}
                      />
                    : <Icon name="question" size="large" />}
                    <div className="following-info">
                      <span className="following-type">
                        {followingItem.type.toUpperCase()}
                      </span>
                      <Link
                        to={
                          followingItem.isDeleted ? "" : (
                            `/${followingItem.type}s/${followingItem._id}`
                          )
                        }
                        className={`following-name ${followingItem.isDeleted ? "following-is-deleted" : ""}`}
                      >
                        {getFollowingItemTitle(followingItem)}
                      </Link>
                    </div>
                  </FollowingContext>
                </Table.Cell>
                <Table.Cell width={5}>
                  <div className="following-info">
                    <span className="following-name">
                      {getReasonText(followingItem)}
                    </span>
                    <span className="following-type">
                      On {formatDate(followingItem.createdAt)}
                    </span>
                  </div>
                </Table.Cell>
                {user?._id === profileUser?._id && (
                  <Table.Cell width={2}>
                    {!followingItem.isDeleted && (
                      <Button
                        basic={!followingItem.unfollowed}
                        content={
                          followingItem.unfollowed ?
                            t("users.following.refollow")
                          : t("users.following.unfollow")
                        }
                        loading={followingItem._id === updating}
                        onClick={() => updateFollowing(followingItem)}
                        style={{ width: 120 }}
                      />
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      }
      {hasFollowing ?
        <div className="pagination-container">
          {paginationState.prevPageAvailable ?
            <Button
              size="tiny"
              content={t("generic.previous")}
              onClick={() => setSearchPage(searchPage - 1)}
            />
          : <div />}
          {paginationState.nextPageAvailable ?
            <Button
              size="tiny"
              content={t("generic.next")}
              onClick={() => setSearchPage(searchPage + 1)}
            />
          : <div />}
        </div>
      : null}
    </FollowingContainer>
  );
};

export default ProfileFollowing;
